import React, { useEffect, useMemo, useState } from "react";
import { logError } from "../../utils/logError";

const gunler = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const haftaBaslangicSecenekleri = [
  { value: 1, label: "Pazartesi" },
  { value: 2, label: "Salı" },
  { value: 3, label: "Çarşamba" },
  { value: 4, label: "Perşembe" },
  { value: 5, label: "Cuma" },
  { value: 6, label: "Cumartesi" },
  { value: 0, label: "Pazar" },
];

const firmaOptions = [
  { id: "all", ad: "Tümü" },
  { id: "3", ad: "Inga Burger" },
  { id: "4", ad: "Altı Üstü Köfte" },
  { id: "5", ad: "Toruna Kasap" },
];

function formatDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getCustomWeekStart(referenceDate, selectedWeekday) {
  const start = new Date(referenceDate);
  start.setHours(0, 0, 0, 0);

  const diff = (start.getDay() - Number(selectedWeekday) + 7) % 7;
  start.setDate(start.getDate() - diff);

  return start;
}

function getMonthDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const start = getMonday(firstDay);
  const days = [];

  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }

  return days;
}

function formatCurrency(value) {
  return Number(value || 0).toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
  });
}

function csvCell(value) {
  const text = String(value ?? "").replace(/"/g, '""');
  return `"${text}"`;
}

function downloadCsv(filename, rows) {
  // UTF-8 BOM: Excel'de Türkçe karakterlerin doğru açılması için.
  const csv = "\uFEFF" + rows.map((row) => row.map(csvCell).join(";")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function formatMonthTitle(date) {
  return date.toLocaleDateString("tr-TR", {
    month: "long",
    year: "numeric",
  });
}

function getMonthlyReferenceDate(selectedMonth, todayKey) {
  const [year, month] = selectedMonth.split("-").map(Number);
  const currentMonth = todayKey.slice(0, 7);

  if (selectedMonth === currentMonth) return todayKey;

  const selectedStart = new Date(year, month - 1, 1);
  const today = new Date(`${todayKey}T00:00:00`);

  // Geçmiş aylar için ayın son gününü, gelecek aylar için ayın ilk gününü
  // referans al. Böylece geçmiş raporlar ay sonuna kadar olan veriyi kullanır;
  // gelecek ay tahmini de önceki dönemin verisine dayanır.
  if (selectedStart < new Date(today.getFullYear(), today.getMonth(), 1)) {
    return formatDateKey(new Date(year, month, 0));
  }

  return `${selectedMonth}-01`;
}

export default function SatinalmaTakvimiPage() {
  const today = useMemo(() => new Date(), []);
  const todayKey = useMemo(() => formatDateKey(today), [today]);

  const [selectedFirmaId, setSelectedFirmaId] = useState("all");
  const [selectedDate, setSelectedDate] = useState(todayKey);
  const [weekStartDay, setWeekStartDay] = useState(1);
  const [ordersByDate, setOrdersByDate] = useState({});
  const [weeklyBudget, setWeeklyBudget] = useState(0);
  const [supplierCountsByDate, setSupplierCountsByDate] = useState({});
  const [warnings, setWarnings] = useState([]);
  const [expandedSuppliers, setExpandedSuppliers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState("weekly");
  const [monthlyItems, setMonthlyItems] = useState([]);
  const [monthlyBudget, setMonthlyBudget] = useState(0);
  const [actualMonthlyItems, setActualMonthlyItems] = useState([]);
  const [actualMonthlyBudget, setActualMonthlyBudget] = useState(0);
  const [monthlyDataMode, setMonthlyDataMode] = useState("average");
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyError, setMonthlyError] = useState("");
  const [monthlySort, setMonthlySort] = useState({ key: "tutar", direction: "desc" });
  const [monthlyDisplayMode, setMonthlyDisplayMode] = useState("all");
  const [expandedMonthlySuppliers, setExpandedMonthlySuppliers] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(todayKey.slice(0, 7));

  const year = today.getFullYear();
  const month = today.getMonth();

  const monthDays = useMemo(() => getMonthDays(year, month), [year, month]);

  const selectedMonthDate = useMemo(() => {
    const [selectedYear, selectedMonthNumber] = selectedMonth.split("-").map(Number);
    return new Date(selectedYear, selectedMonthNumber - 1, 1);
  }, [selectedMonth]);

  const monthlyCalendarDays = useMemo(
    () => getMonthDays(selectedMonthDate.getFullYear(), selectedMonthDate.getMonth()),
    [selectedMonthDate]
  );

  const selectedMonthStart = useMemo(
    () => new Date(selectedMonthDate.getFullYear(), selectedMonthDate.getMonth(), 1),
    [selectedMonthDate]
  );

  const selectedMonthEnd = useMemo(
    () => new Date(selectedMonthDate.getFullYear(), selectedMonthDate.getMonth() + 1, 0),
    [selectedMonthDate]
  );

  const monthlyReferenceDate = useMemo(
    () => getMonthlyReferenceDate(selectedMonth, todayKey),
    [selectedMonth, todayKey]
  );

  const planStart = useMemo(
    () => getCustomWeekStart(today, weekStartDay),
    [today, weekStartDay]
  );

  const planEnd = useMemo(() => {
    const end = new Date(planStart);
    end.setDate(end.getDate() + 6);
    return end;
  }, [planStart]);

  useEffect(() => {
    const controller = new AbortController();

    async function loadWeeklyPlan() {
      setLoading(true);
      setError("");

      try {
        // JavaScript getDay(): Pazar=0, Pazartesi=1, ...
        // Python date.weekday(): Pazartesi=0, Salı=1, ..., Pazar=6
        const pythonWeekStartDay = (Number(weekStartDay) + 6) % 7;

        const params = new URLSearchParams({
          reference_date: todayKey,
          week_start_day: String(pythonWeekStartDay),
        });

        if (selectedFirmaId !== "all") {
          params.set("lokasyon_id", selectedFirmaId);
        }

        const response = await fetch(`${API_URL}/api/satinalma/haftalik-plan3?${params.toString()}`,
          {
            signal: controller.signal,
            headers: {
              ...getAuthHeaders(),
              Accept: "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Haftalık plan alınamadı (${response.status})`);
        }

        const data = await response.json();

        setOrdersByDate(data.ordersByDate || {});
        setWeeklyBudget(Number(data.weeklyBudget || 0));
        setSupplierCountsByDate(data.supplierCountsByDate || {});
        setWarnings(data.warnings || []);

        const returnedStart = data.weekStart || formatDateKey(planStart);
        const returnedEnd = data.weekEnd || formatDateKey(planEnd);

        setSelectedDate((current) =>
          current < returnedStart || current > returnedEnd
            ? returnedStart
            : current
        );
      } catch (err) {
        if (err.name !== "AbortError") {
          logError("Satın alma planı alınamadı", err);
          setError(err.message || "Veriler alınırken bir hata oluştu.");
          setOrdersByDate({});
          setWeeklyBudget(0);
          setSupplierCountsByDate({});
          setWarnings([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    loadWeeklyPlan();

    return () => controller.abort();
  }, [todayKey, weekStartDay, selectedFirmaId, planStart, planEnd]);

  useEffect(() => {
    if (viewMode !== "monthly") return undefined;

    const controller = new AbortController();

    async function loadMonthlyPlan() {
      setMonthlyLoading(true);
      setMonthlyError("");
      try {
        const params = new URLSearchParams({ reference_date: monthlyReferenceDate });
        if (selectedFirmaId !== "all") params.set("lokasyon_id", selectedFirmaId);

        const response = await fetch(`${API_URL}/api/satinalma/aylik-plan?${params.toString()}`, {
          signal: controller.signal,
          headers: { ...getAuthHeaders(), Accept: "application/json" },
        });
        if (!response.ok) throw new Error(`Aylık plan alınamadı (${response.status})`);

        const data = await response.json();
        setMonthlyItems(data.items || []);
        setMonthlyBudget(Number(data.monthlyBudget || 0));
        setActualMonthlyItems(data.actualItems || []);
        setActualMonthlyBudget(Number(data.actualMonthlyBudget || 0));
        setWarnings(data.warnings || []);
      } catch (err) {
        if (err.name !== "AbortError") {
          logError("Aylık satın alma planı alınamadı", err);
          setMonthlyError(err.message || "Aylık veriler alınırken bir hata oluştu.");
          setMonthlyItems([]);
          setMonthlyBudget(0);
          setActualMonthlyItems([]);
          setActualMonthlyBudget(0);
        }
      } finally {
        if (!controller.signal.aborted) setMonthlyLoading(false);
      }
    }

    loadMonthlyPlan();
    return () => controller.abort();
  }, [viewMode, monthlyReferenceDate, selectedFirmaId]);

  const activeMonthlyItems = monthlyDataMode === "actual" ? actualMonthlyItems : monthlyItems;
  const activeMonthlyBudget = monthlyDataMode === "actual" ? actualMonthlyBudget : monthlyBudget;

  const sortedMonthlyItems = useMemo(() => {
    const items = [...activeMonthlyItems];
    const multiplier = monthlySort.direction === "desc" ? -1 : 1;

    items.sort((a, b) => {
      if (monthlySort.key === "tedarikci_unvan") {
        return String(a.tedarikci_unvan || "").localeCompare(
          String(b.tedarikci_unvan || ""),
          "tr",
          { sensitivity: "base" }
        ) * multiplier;
      }

      return (Number(a[monthlySort.key] || 0) - Number(b[monthlySort.key] || 0)) * multiplier;
    });

    return items;
  }, [activeMonthlyItems, monthlySort]);

  const monthlySupplierGroups = useMemo(() => {
    const groups = new Map();

    activeMonthlyItems.forEach((item) => {
      const key = String(item.tedarikci_id ?? item.tedarikci_unvan ?? "BILINMEYEN");
      if (!groups.has(key)) {
        groups.set(key, {
          id: item.tedarikci_id ?? key,
          unvan: item.tedarikci_unvan || "Tedarikçi belirtilmemiş",
          items: [],
          total: 0,
        });
      }

      const group = groups.get(key);
      group.items.push(item);
      group.total += Number(item.tutar || 0);
    });

    return [...groups.values()].sort((a, b) =>
      String(a.unvan || "").localeCompare(String(b.unvan || ""), "tr", { sensitivity: "base" })
    );
  }, [activeMonthlyItems]);

  function toggleMonthlySupplier(supplierId) {
    const key = `${selectedMonth}-${supplierId}`;
    setExpandedMonthlySuppliers((current) => ({
      ...current,
      [key]: !current[key],
    }));
  }

  function changeSelectedMonth(offset) {
    const next = new Date(
      selectedMonthDate.getFullYear(),
      selectedMonthDate.getMonth() + offset,
      1
    );
    setSelectedMonth(formatDateKey(next).slice(0, 7));
  }

  function toggleMonthlySort(key) {
    setMonthlySort((current) => {
      if (current.key === key) {
        return { key, direction: current.direction === "desc" ? "asc" : "desc" };
      }

      return {
        key,
        direction: key === "tedarikci_unvan" ? "asc" : "desc",
      };
    });
  }

  function handleMonthlyCsvDownload() {
    if (sortedMonthlyItems.length === 0) return;
    const header = ["Ürün", "Tedarikçi", "Adet", "Birim", "Birim Fiyat", "Tutar"];
    const rows = sortedMonthlyItems.map((item) => [
      item.malzeme_ad, item.tedarikci_unvan, item.adet, item.birim, item.birim_fiyat, item.tutar,
    ]);
    const tur = monthlyDataMode === "actual" ? "gercek-alimlar" : "gecmis-ortalamasi";
    downloadCsv(`aylik-satinalma-${tur}-${selectedMonth}.csv`, [header, ...rows]);
  }

  async function handleWeekStartChange(event) {
    const newValue = Number(event.target.value);
    setWeekStartDay(newValue);

    try {
      await fetch(`${API_URL}/api/satinalma/ayarlar/hafta-baslangici`, {
        method: "PUT",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ week_start_day: newValue }),
      });
    } catch (err) {
      logError("Hafta başlangıcı kaydedilemedi", err);
    }
  }

  const selectedItems = useMemo(
    () =>
      [...(ordersByDate[selectedDate] || [])].sort(
        (a, b) => Number(b.tuketim_ort || 0) - Number(a.tuketim_ort || 0)
      ),
    [ordersByDate, selectedDate]
  );

  const selectedTotal = useMemo(
    () => selectedItems.reduce((sum, item) => sum + Number(item.tutar || 0), 0),
    [selectedItems]
  );

  const selectedSupplierGroups = useMemo(() => {
    const groups = new Map();
    selectedItems.forEach((item) => {
      const key = String(item.tedarikci_id);
      if (!groups.has(key)) {
        groups.set(key, {
          id: item.tedarikci_id,
          unvan: item.tedarikci_unvan,
          siparisUzerine: Boolean(item.siparis_uzerine),
          items: [],
          total: 0,
        });
      }
      const group = groups.get(key);
      group.items.push(item);
      group.total += Number(item.tutar || 0);
    });
    return [...groups.values()].sort((a, b) =>
      String(a.unvan || "").localeCompare(String(b.unvan || ""), "tr")
    );
  }, [selectedItems]);

  function toggleSupplier(supplierId) {
    const key = `${selectedDate}-${supplierId}`;
    setExpandedSuppliers((current) => ({ ...current, [key]: !current[key] }));
  }

  function handleCsvDownload() {
    if (selectedItems.length === 0) return;

    const header = [
      "Tarih",
      "Tedarikçi",
      "Malzeme Kodu",
      "Malzeme",
      "Lokasyon",
      "Kapsanan Günler",
      "Minimum Stok",
      "Satış Ort.",
      "Alış Ort.",
      "Alınacak Miktar",
      "Birim",
      "Birim Fiyatı",
      "Tutar",
      "Plan Tipi",
    ];

    const rows = selectedItems.map((item) => [
      selectedDate,
      item.tedarikci_unvan,
      item.malzeme_kod,
      item.malzeme_ad,
      item.lokasyon_ad,
      (item.kapsanan_gunler || [])
        .map((g) => `${g.gun}: ${g.ortalama}`)
        .join(" + "),
      item.min_stok,
      item.tuketim_ort,
      item.siparis_ort,
      item.alinacak_miktar,
      item.birim,
      item.son_fiyat,
      item.tutar,
      item.siparis_uzerine ? "Sipariş üzerine" : "Sabit teslimat günü",
    ]);

    downloadCsv(`satinalma-plani-${selectedDate}.csv`, [header, ...rows]);
  }

  return (
    <div style={{ padding: 20 }}>
      <div
        style={{
          maxWidth: 1150,
          margin: "0 auto 14px",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div style={labelStyle}>Firma</div>
            <select
              value={selectedFirmaId}
              onChange={(event) => setSelectedFirmaId(event.target.value)}
              style={selectStyle}
            >
              {firmaOptions.map((firma) => (
                <option key={firma.id} value={firma.id}>
                  {firma.ad}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div style={labelStyle}>Firma haftasının başlangıç günü</div>
            <select
              value={weekStartDay}
              onChange={handleWeekStartChange}
              style={selectStyle}
            >
              {haftaBaslangicSecenekleri.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ fontSize: 14, color: "#475569", fontWeight: 700 }}>
          Plan dönemi: {viewMode === "weekly"
            ? `${formatDateKey(planStart)} – ${formatDateKey(planEnd)}`
            : `${formatDateKey(selectedMonthStart)} – ${formatDateKey(selectedMonthEnd)}`}
        </div>
      </div>

      <div style={{ maxWidth: 1150, margin: "0 auto 16px", display: "flex", justifyContent: "center" }}>
        <div style={{ display: "inline-flex", padding: 4, background: "#e2e8f0", borderRadius: 12 }}>
          {[{ key: "weekly", label: "Haftalık" }, { key: "monthly", label: "Aylık" }].map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setViewMode(option.key)}
              style={{
                border: 0, borderRadius: 9, padding: "9px 22px", fontWeight: 800, cursor: "pointer",
                background: viewMode === option.key ? "#ffffff" : "transparent",
                color: viewMode === option.key ? "#0f172a" : "#64748b",
                boxShadow: viewMode === option.key ? "0 1px 4px rgba(0,0,0,0.12)" : "none",
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {(viewMode === "weekly" ? error : monthlyError) && (
        <div
          style={{
            maxWidth: 1150,
            margin: "0 auto 14px",
            padding: 12,
            borderRadius: 10,
            background: "#fee2e2",
            color: "#991b1b",
          }}
        >
          {viewMode === "weekly" ? error : monthlyError}
        </div>
      )}

      {viewMode === "weekly" ? (
        <>
      <div
        style={{
          maxWidth: 1150,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "minmax(650px, 1fr) 300px",
          gap: 18,
          alignItems: "start",
        }}
      >
        <div
          style={{
            background: "#f8fafc",
            borderRadius: 18,
            padding: 18,
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              textAlign: "center",
              fontSize: 22,
              fontWeight: 700,
              marginBottom: 12,
              textTransform: "capitalize",
            }}
          >
            {formatMonthTitle(today)}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 6,
              marginBottom: 6,
            }}
          >
            {gunler.map((gun) => (
              <div
                key={gun}
                style={{
                  textAlign: "center",
                  fontWeight: 700,
                  color: "#64748b",
                  fontSize: 13,
                }}
              >
                {gun}
              </div>
            ))}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 6,
            }}
          >
            {monthDays.map((date) => {
              const key = formatDateKey(date);
              const isToday = key === todayKey;
              const isCurrentMonth = date.getMonth() === month;
              const hasOrder = Boolean(ordersByDate[key]?.length);
              const isPlanRange = date >= planStart && date <= planEnd;

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedDate(key)}
                  style={{
                    height: 54,
                    border:
                      selectedDate === key
                        ? "2px solid #2563eb"
                        : "1px solid #e2e8f0",
                    borderRadius: 12,
                    background: hasOrder
                      ? "#f59e0b"
                      : isPlanRange
                      ? "#ffffff"
                      : "#e5e7eb",
                    opacity: isCurrentMonth ? 1 : 0.35,
                    cursor: "pointer",
                    position: "relative",
                    fontSize: 15,
                    fontWeight: 700,
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      fontSize: 17,
                      fontWeight: 700,
                      boxSizing: "border-box",
                      borderRadius: "50%",
                      border: isToday ? "3px solid #dc2626" : "none",
                      background: "transparent",
                    }}
                  >
                    {date.getDate()}
                  </span>

                  {hasOrder && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: 3,
                        left: 0,
                        right: 0,
                        fontSize: 10,
                        fontWeight: 600,
                        color: "#7c2d12",
                      }}
                    >
                      {supplierCountsByDate[key] || 0} tedarikçi
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          <InfoCard
            title="Haftalık Satın Alma Bütçesi"
            value={loading ? "Hesaplanıyor..." : formatCurrency(weeklyBudget)}
          />

          <InfoCard
            title="Seçili Gün Toplamı"
            value={loading ? "Hesaplanıyor..." : formatCurrency(selectedTotal)}
          />
        </div>
      </div>

      {warnings.length > 0 && (
        <div
          style={{
            maxWidth: 1150,
            margin: "16px auto 0",
            padding: 12,
            borderRadius: 10,
            background: "#fff7ed",
            color: "#9a3412",
            fontWeight: 700,
          }}
          title={warnings.join("\n")}
        >
          ⚠ {warnings.length} tedarikçinin teslimat ayarı eksik. Sabit teslim günü
          seçilmeli veya “sipariş üzerine” alanı açılmalıdır.
        </div>
      )}

      <div
        style={{
          maxWidth: 1150,
          margin: "20px auto 0",
          display: "grid",
          gap: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 800 }}>
            {selectedDate} tarihinde çalışılacak tedarikçiler
          </div>

          <button
            type="button"
            onClick={handleCsvDownload}
            disabled={loading || selectedItems.length === 0}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid #15803d",
              background:
                loading || selectedItems.length === 0 ? "#e2e8f0" : "#16a34a",
              color:
                loading || selectedItems.length === 0 ? "#64748b" : "#ffffff",
              fontWeight: 800,
              cursor:
                loading || selectedItems.length === 0 ? "not-allowed" : "pointer",
            }}
          >
            CSV Olarak İndir
          </button>
        </div>

        {loading ? (
          <div style={emptyPanelStyle}>Veriler hazırlanıyor...</div>
        ) : selectedSupplierGroups.length === 0 ? (
          <div style={emptyPanelStyle}>Bu gün için sipariş veya teslimat önerisi yok.</div>
        ) : (
          selectedSupplierGroups.map((supplier) => {
            const expandKey = `${selectedDate}-${supplier.id}`;
            const expanded = Boolean(expandedSuppliers[expandKey]);
            return (
              <div key={supplier.id} style={supplierCardStyle}>
                <button type="button" onClick={() => toggleSupplier(supplier.id)} style={supplierHeaderStyle}>
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: 17, fontWeight: 800 }}>
                      {expanded ? "▼" : "▶"} {supplier.unvan}
                    </div>
                    <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                      {supplier.items.length} malzeme · {supplier.siparisUzerine ? "Sipariş üzerine (stok eşiği)" : "Sabit teslimat günü"}
                    </div>
                  </div>
                  <div style={{ fontWeight: 800 }}>{formatCurrency(supplier.total)}</div>
                </button>

                {expanded && (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", minWidth: 1050, borderCollapse: "collapse" }}>
                      <thead style={{ background: "#f1f5f9" }}>
                        <tr>
                          <th style={th}>Malzeme</th>
                          <th style={th}>Lokasyon</th>
                          <th style={th}>Kapsanan Günler</th>
                          <th style={thRight}>Min. Stok</th>
                          <th style={thRight}>Satış Ort.</th>
                          <th style={thRight}>Alış Ort.</th>
                          <th style={thRight}>Alınacak</th>
                          <th style={thRight}>Birim Fiyatı</th>
                          <th style={thRight}>Tutar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {supplier.items.map((item) => (
                          <tr key={`${selectedDate}-${item.lokasyon_id}-${item.malzeme_id}`}>
                            <td style={td}>
                              <b>{item.malzeme_ad}</b>
                              <div style={{ fontSize: 12, color: "#64748b" }}>{item.malzeme_kod} · {item.birim}</div>
                            </td>
                            <td style={td}>{item.lokasyon_ad}</td>
                            <td style={td}>
                              {(item.kapsanan_gunler || []).map((g) => `${g.gun}: ${g.ortalama}`).join(" + ")}
                              {item.sonraki_teslim_tarihi && (
                                <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>
                                  Sonraki teslim: {item.sonraki_teslim_tarihi}
                                </div>
                              )}
                            </td>
                            <td style={tdRight}>{item.min_stok}</td>
                            <td style={tdRight}>{item.tuketim_ort}</td>
                            <td style={tdRight}>{item.siparis_ort}</td>
                            <td style={tdRight}><b>{item.alinacak_miktar} {item.birim}</b></td>
                            <td style={tdRight}>{formatCurrency(item.son_fiyat)}</td>
                            <td style={tdRight}>{formatCurrency(item.tutar)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })
        )}

        <div style={infoStyle}>
          <b>Bilgilendirme:</b> Satış Ort., seçilen gün için lokasyon bazındaki ortalama tüketimi; Alış Ort., aynı haftanın gününde geçmişte verilen siparişlerin ortalama miktarını gösterir. Alınacak miktar, sabit günlü tedarikçilerde bir sonraki teslim gününe kadar olan günlük kullanım ortalamalarının toplamıdır. Mevcut stok bu miktardan düşülmez.
        </div>
      </div>
        </>
      ) : (
        <div style={{ maxWidth: 1150, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, textTransform: "capitalize" }}>{formatMonthTitle(selectedMonthDate)} Satın Alma Planı</div>
              <div style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>Ay içindeki satın alma önerileri ürün ve tedarikçi bazında toplam gösterilir.</div>
            </div>
            <div style={{ display: "flex", alignItems: "end", gap: 10, flexWrap: "wrap" }}>
              <button type="button" onClick={handleMonthlyCsvDownload} disabled={monthlyLoading || sortedMonthlyItems.length === 0}
              style={{ padding: "10px 14px", borderRadius: 10, border: "1px solid #15803d", background: monthlyLoading || sortedMonthlyItems.length === 0 ? "#e2e8f0" : "#16a34a", color: monthlyLoading || sortedMonthlyItems.length === 0 ? "#64748b" : "#fff", fontWeight: 800, cursor: monthlyLoading || sortedMonthlyItems.length === 0 ? "not-allowed" : "pointer" }}>
              CSV Olarak İndir
              </button>
            </div>
          </div>

          <div
            style={{
              background: "#f8fafc",
              borderRadius: 18,
              padding: 18,
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              marginBottom: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 14,
                marginBottom: 12,
              }}
            >
              <button
                type="button"
                onClick={() => changeSelectedMonth(-1)}
                style={monthNavButtonStyle}
                aria-label="Önceki ay"
              >
                ‹
              </button>
              <div
                style={{
                  minWidth: 220,
                  textAlign: "center",
                  fontSize: 22,
                  fontWeight: 700,
                  textTransform: "capitalize",
                }}
              >
                {formatMonthTitle(selectedMonthDate)}
              </div>
              <button
                type="button"
                onClick={() => changeSelectedMonth(1)}
                style={monthNavButtonStyle}
                aria-label="Sonraki ay"
              >
                ›
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 6,
                marginBottom: 6,
              }}
            >
              {gunler.map((gun) => (
                <div
                  key={gun}
                  style={{
                    textAlign: "center",
                    fontWeight: 700,
                    color: "#64748b",
                    fontSize: 13,
                  }}
                >
                  {gun}
                </div>
              ))}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 6,
              }}
            >
              {monthlyCalendarDays.map((date) => {
                const key = formatDateKey(date);
                const isToday = key === todayKey;
                const isSelectedMonth =
                  date.getFullYear() === selectedMonthDate.getFullYear() &&
                  date.getMonth() === selectedMonthDate.getMonth();

                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSelectedMonth(key.slice(0, 7))}
                    style={{
                      height: 52,
                      border: isSelectedMonth ? "1px solid #cbd5e1" : "1px solid #e2e8f0",
                      borderRadius: 12,
                      background: isSelectedMonth ? "#ffffff" : "#e5e7eb",
                      opacity: isSelectedMonth ? 1 : 0.4,
                      cursor: "pointer",
                      fontSize: 15,
                      fontWeight: 700,
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 38,
                        height: 38,
                        borderRadius: "50%",
                        border: isToday ? "3px solid #dc2626" : "none",
                        boxSizing: "border-box",
                      }}
                    >
                      {date.getDate()}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div
            style={{
              marginBottom: 14,
              display: "flex",
              alignItems: "stretch",
              justifyContent: "space-between",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            <div style={{ minWidth: 300, maxWidth: 360, flex: "1 1 300px" }}>
              <InfoCard
                title={monthlyDataMode === "actual" ? "Seçili Ay Gerçek Alım Tutarı" : "Aylık Planlanan Tutar"}
                value={monthlyLoading ? "Hesaplanıyor..." : formatCurrency(activeMonthlyBudget)}
              />
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <div
                style={{
                  display: "inline-flex",
                  alignSelf: "center",
                  padding: 4,
                  background: "#e2e8f0",
                  borderRadius: 12,
                }}
              >
                {[
                  { key: "average", label: "Geçmiş Ortalaması / Plan" },
                  { key: "actual", label: "Seçili Ay Gerçek Alımlar" },
                ].map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setMonthlyDataMode(option.key)}
                    style={{
                      border: 0,
                      borderRadius: 9,
                      padding: "9px 16px",
                      fontWeight: 800,
                      cursor: "pointer",
                      background: monthlyDataMode === option.key ? "#ffffff" : "transparent",
                      color: monthlyDataMode === option.key ? "#0f172a" : "#64748b",
                      boxShadow: monthlyDataMode === option.key ? "0 1px 4px rgba(0,0,0,0.12)" : "none",
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <div
                style={{
                  display: "inline-flex",
                  alignSelf: "center",
                  padding: 4,
                  background: "#e2e8f0",
                  borderRadius: 12,
                }}
              >
                {[
                  { key: "all", label: "Tüm Liste" },
                  { key: "supplier", label: "Tedarikçi" },
                ].map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setMonthlyDisplayMode(option.key)}
                  style={{
                    border: 0,
                    borderRadius: 9,
                    padding: "9px 18px",
                    fontWeight: 800,
                    cursor: "pointer",
                    background: monthlyDisplayMode === option.key ? "#ffffff" : "transparent",
                    color: monthlyDisplayMode === option.key ? "#0f172a" : "#64748b",
                    boxShadow: monthlyDisplayMode === option.key ? "0 1px 4px rgba(0,0,0,0.12)" : "none",
                  }}
                >
                  {option.label}
                </button>
                ))}
              </div>
            </div>
          </div>

          {monthlyLoading ? (
            <div style={emptyPanelStyle}>Aylık veriler hazırlanıyor...</div>
          ) : sortedMonthlyItems.length === 0 ? (
            <div style={emptyPanelStyle}>
              {monthlyDataMode === "actual"
                ? "Seçili ay için onaylanmış gerçek alım kaydı yok."
                : "Bu ay için satın alma önerisi yok."}
            </div>
          ) : monthlyDisplayMode === "supplier" ? (
            <div style={{ display: "grid", gap: 12 }}>
              {monthlySupplierGroups.map((supplier) => {
                const expandKey = `${selectedMonth}-${supplier.id}`;
                const expanded = Boolean(expandedMonthlySuppliers[expandKey]);

                return (
                  <div key={supplier.id} style={supplierCardStyle}>
                    <button
                      type="button"
                      onClick={() => toggleMonthlySupplier(supplier.id)}
                      style={supplierHeaderStyle}
                    >
                      <div style={{ textAlign: "left" }}>
                        <div style={{ fontSize: 17, fontWeight: 800 }}>
                          {expanded ? "▼" : "▶"} {supplier.unvan}
                        </div>
                        <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                          {supplier.items.length} malzeme · {monthlyDataMode === "actual" ? "Gerçek alım toplamı" : "Aylık plan toplamı"}
                        </div>
                      </div>
                      <div style={{ fontWeight: 800 }}>{formatCurrency(supplier.total)}</div>
                    </button>

                    {expanded && (
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", minWidth: 760, borderCollapse: "collapse" }}>
                          <thead style={{ background: "#f1f5f9" }}>
                            <tr>
                              <th style={th}>Ürün</th>
                              <th style={{ ...thRight, cursor: "pointer" }} onClick={() => toggleMonthlySort("adet")}>
                                Adet {monthlySort.key === "adet" ? (monthlySort.direction === "desc" ? "▼" : "▲") : "↕"}
                              </th>
                              <th style={th}>Birim</th>
                              <th style={{ ...thRight, cursor: "pointer" }} onClick={() => toggleMonthlySort("birim_fiyat")}>
                                Birim Fiyat {monthlySort.key === "birim_fiyat" ? (monthlySort.direction === "desc" ? "▼" : "▲") : "↕"}
                              </th>
                              <th style={{ ...thRight, cursor: "pointer" }} onClick={() => toggleMonthlySort("tutar")}>
                                Tutar {monthlySort.key === "tutar" ? (monthlySort.direction === "desc" ? "▼" : "▲") : "↕"}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {supplier.items
                              .slice()
                              .sort((a, b) => {
                                const multiplier = monthlySort.direction === "desc" ? -1 : 1;
                                return (Number(a[monthlySort.key] || 0) - Number(b[monthlySort.key] || 0)) * multiplier;
                              })
                              .map((item) => (
                                <tr key={`${supplier.id}-${item.malzeme_id}-${item.birim}`}>
                                  <td style={td}>
                                    <b>{item.malzeme_ad}</b>
                                    <div style={{ fontSize: 12, color: "#64748b" }}>{item.malzeme_kod}</div>
                                  </td>
                                  <td style={tdRight}><b>{item.adet}</b></td>
                                  <td style={td}>{item.birim}</td>
                                  <td style={tdRight}>{formatCurrency(item.birim_fiyat)}</td>
                                  <td style={tdRight}><b>{formatCurrency(item.tutar)}</b></td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", overflowX: "auto" }}>
              <table style={{ width: "100%", minWidth: 900, borderCollapse: "collapse" }}>
                <thead style={{ background: "#f1f5f9" }}>
                  <tr>
                    <th style={th}>Ürün</th>
                    <th style={{ ...th, cursor: "pointer" }} onClick={() => toggleMonthlySort("tedarikci_unvan")}>
                      Tedarikçi {monthlySort.key === "tedarikci_unvan" ? (monthlySort.direction === "desc" ? "▼" : "▲") : "↕"}
                    </th>
                    <th style={{ ...thRight, cursor: "pointer" }} onClick={() => toggleMonthlySort("adet")}>
                      Adet {monthlySort.key === "adet" ? (monthlySort.direction === "desc" ? "▼" : "▲") : "↕"}
                    </th>
                    <th style={th}>Birim</th>
                    <th style={{ ...thRight, cursor: "pointer" }} onClick={() => toggleMonthlySort("birim_fiyat")}>
                      Birim Fiyat {monthlySort.key === "birim_fiyat" ? (monthlySort.direction === "desc" ? "▼" : "▲") : "↕"}
                    </th>
                    <th style={{ ...thRight, cursor: "pointer" }} onClick={() => toggleMonthlySort("tutar")}>
                      Tutar {monthlySort.key === "tutar" ? (monthlySort.direction === "desc" ? "▼" : "▲") : "↕"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedMonthlyItems.map((item) => (
                    <tr key={`${item.malzeme_id}-${item.tedarikci_id}-${item.birim}`}>
                      <td style={td}><b>{item.malzeme_ad}</b><div style={{ fontSize: 12, color: "#64748b" }}>{item.malzeme_kod}</div></td>
                      <td style={td}>{item.tedarikci_unvan}</td>
                      <td style={tdRight}><b>{item.adet}</b></td>
                      <td style={td}>{item.birim}</td>
                      <td style={tdRight}>{formatCurrency(item.birim_fiyat)}</td>
                      <td style={tdRight}><b>{formatCurrency(item.tutar)}</b></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const monthNavButtonStyle = {
  width: 38,
  height: 38,
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  color: "#0f172a",
  fontSize: 26,
  lineHeight: 1,
  fontWeight: 700,
  cursor: "pointer",
};

function InfoCard({ title, value }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 14,
        padding: 16,
        boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
      }}
    >
      <div style={{ color: "#64748b", fontSize: 13 }}>{title}</div>
      <div style={{ fontSize: 24, fontWeight: 800 }}>{value}</div>
    </div>
  );
}

const labelStyle = {
  fontSize: 13,
  color: "#64748b",
  marginBottom: 5,
};

const selectStyle = {
  minWidth: 180,
  padding: "10px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  background: "#fff",
  fontSize: 14,
  fontWeight: 700,
};

const emptyPanelStyle = {
  padding: 24,
  background: "#fff",
  borderRadius: 14,
  boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
  color: "#64748b",
  textAlign: "center",
};

const supplierCardStyle = {
  background: "#fff",
  borderRadius: 14,
  boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const supplierHeaderStyle = {
  width: "100%",
  padding: 16,
  border: 0,
  background: "#fff",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 16,
  cursor: "pointer",
};

const infoStyle = {
  padding: "12px 14px",
  borderRadius: 12,
  background: "#f8fafc",
  color: "#475569",
  fontSize: 13,
  lineHeight: 1.6,
};

const th = {
  padding: "10px",
  textAlign: "left",
  fontSize: 13,
  color: "#334155",
  borderBottom: "1px solid #e2e8f0",
};

const thRight = { ...th, textAlign: "right" };

const td = {
  padding: "10px",
  borderBottom: "1px solid #e2e8f0",
  fontSize: 14,
};

const tdRight = { ...td, textAlign: "right" };

const emptyCellStyle = {
  padding: 22,
  textAlign: "center",
  color: "#64748b",
};
